import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  CreditCard, Search, Loader2, ArrowLeft, CheckCircle2,
  AlertCircle, DollarSign, ReceiptText,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function MakePaymentPage() {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState("lookup"); // lookup | confirm | pay-by-amount
  const [confirmation, setConfirmation] = useState("");
  const [lastName, setLastName] = useState("");

  // Auto-fill confirmation from ?conf= URL param (deep link from WhatsApp/Email)
  useEffect(() => {
    const c = searchParams.get("conf") || searchParams.get("confirmation");
    if (c) setConfirmation(c.toUpperCase());
  }, [searchParams]);
  const [looking, setLooking] = useState(false);
  const [booking, setBooking] = useState(null);
  const [amount, setAmount] = useState("");
  const [email, setEmail] = useState("");
  const [paying, setPaying] = useState(false);

  const lookup = async () => {
    if (!confirmation.trim()) {
      toast.error("Enter a confirmation number");
      return;
    }
    setLooking(true);
    try {
      const { data } = await axios.post(`${API}/api/make-payment/lookup`, {
        confirmation: confirmation.trim(),
        last_name: lastName.trim(),
      });
      setBooking(data);
      setAmount(String(data.balance.toFixed(2)));
      setStep("confirm");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Booking not found");
    } finally {
      setLooking(false);
    }
  };

  const startCheckout = async () => {
    if (!booking) return;
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    setPaying(true);
    try {
      const { data } = await axios.post(`${API}/api/make-payment/checkout`, {
        booking_id: booking.booking_id,
        amount: amt,
        customer_email: email,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout");
      setPaying(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 py-10 px-4">
      <div className="max-w-md mx-auto">
        <Link to="/" className="inline-flex items-center text-sm text-muted-foreground mb-4 hover:text-green-700">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to home
        </Link>

        <Card className="shadow-lg border-green-100" data-testid="make-payment-card">
          <CardHeader className="bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-t-lg">
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" /> Make a Payment
            </CardTitle>
            <p className="text-sm text-green-50 mt-1">
              Pay your balance using your booking confirmation number
            </p>
          </CardHeader>

          <CardContent className="pt-6 space-y-4">
            {step === "lookup" && (
              <>
                <div>
                  <Label htmlFor="conf">Confirmation Number</Label>
                  <Input
                    id="conf"
                    value={confirmation}
                    onChange={(e) => setConfirmation(e.target.value.toUpperCase())}
                    placeholder="e.g. 9106C6EC"
                    autoFocus
                    onKeyDown={(e) => e.key === "Enter" && lookup()}
                    data-testid="payment-confirmation-input"
                  />
                </div>
                <div>
                  <Label htmlFor="lname">Last Name (optional)</Label>
                  <Input
                    id="lname"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="For added security"
                    onKeyDown={(e) => e.key === "Enter" && lookup()}
                    data-testid="payment-lastname-input"
                  />
                </div>
                <Button
                  onClick={lookup}
                  disabled={looking}
                  className="w-full bg-green-600 hover:bg-green-700"
                  data-testid="payment-lookup-btn"
                >
                  {looking ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
                  Find My Booking
                </Button>
                <p className="text-xs text-muted-foreground text-center">
                  Your confirmation # is in your booking email — e.g. <span className="font-mono font-bold">EASY-XXXXX</span>.
                </p>
              </>
            )}

            {step === "confirm" && booking && (
              <>
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200 space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="font-semibold flex items-center gap-1">
                      <ReceiptText className="w-4 h-4 text-green-700" /> #{booking.confirmation}
                    </p>
                    {booking.payment_status === "paid" ? (
                      <Badge className="bg-green-600 text-white"><CheckCircle2 className="w-3 h-3 mr-1" />Paid</Badge>
                    ) : (
                      <Badge className="bg-orange-500 text-white">{booking.payment_status}</Badge>
                    )}
                  </div>
                  <p className="text-sm"><span className="text-muted-foreground">Name:</span> {booking.passenger_name}</p>
                  <p className="text-sm"><span className="text-muted-foreground">Date:</span> {booking.travel_date} at {booking.travel_time}</p>
                  <p className="text-sm truncate"><span className="text-muted-foreground">Trip:</span> {booking.pickup_location} → {booking.dropoff_location}</p>
                  <div className="grid grid-cols-3 gap-2 text-center pt-3 border-t border-green-200">
                    <div>
                      <p className="text-[10px] uppercase text-muted-foreground">Total</p>
                      <p className="font-bold">${booking.total_owed.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase text-muted-foreground">Paid</p>
                      <p className="font-bold text-green-700">${booking.amount_paid.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase text-muted-foreground">Balance</p>
                      <p className="font-bold text-orange-700" data-testid="balance-amount">${booking.balance.toFixed(2)}</p>
                    </div>
                  </div>
                </div>

                {booking.balance <= 0 ? (
                  <div className="bg-green-50 border border-green-300 rounded-lg p-4 text-center" data-testid="zero-balance-msg">
                    <CheckCircle2 className="w-8 h-8 text-green-600 mx-auto mb-2" />
                    <p className="font-semibold text-green-700">Nothing owed on this booking</p>
                    <p className="text-xs text-muted-foreground mt-1">You're all set! Have a safe trip.</p>
                  </div>
                ) : (
                  <>
                    <div>
                      <Label htmlFor="amt">Amount to Pay (USD)</Label>
                      <Input
                        id="amt"
                        type="number"
                        step="0.01"
                        min="1"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        className="text-lg font-bold"
                        data-testid="payment-amount-input"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Defaulting to full balance — edit if you want to pay a different amount.
                      </p>
                    </div>
                    <div>
                      <Label htmlFor="email">Email (for receipt)</Label>
                      <Input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@email.com"
                        data-testid="payment-email-input"
                      />
                    </div>
                    <Button
                      onClick={startCheckout}
                      disabled={paying || !amount || parseFloat(amount) <= 0}
                      className="w-full bg-green-600 hover:bg-green-700 h-12 text-base font-bold"
                      data-testid="payment-pay-btn"
                    >
                      {paying ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <DollarSign className="w-5 h-5 mr-2" />}
                      Pay ${parseFloat(amount || 0).toFixed(2)} Securely
                    </Button>
                    <p className="text-xs text-center text-muted-foreground">
                      Powered by Stripe · Card details never touch our servers
                    </p>
                  </>
                )}

                <Button
                  variant="ghost"
                  className="w-full text-sm"
                  onClick={() => { setStep("lookup"); setBooking(null); }}
                  data-testid="payment-back-btn"
                >
                  Look up a different booking
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        <div className="mt-6 text-center text-xs text-muted-foreground" data-testid="payment-help">
          <p>Trouble paying? WhatsApp us at <a href="https://wa.me/12625311089" className="text-green-700 underline">+1 (262) 531-1089</a></p>
        </div>
      </div>
    </div>
  );
}
